
import { Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { MessageService } from 'primeng/api';

import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

import { AmplificationStatusAPIEnum } from '../../../../../api/distributions/enum/amplification-status-api.enum';
import { MediaServiceApi } from '../../../../../api/mediaService/media-service.api';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';

import { AmplifyContentService } from '../../../../../Amplify/AmplifyShared/services/amplify-content.service';

import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { attributeTypesEnum } from '../../../../../Common/Misc/enums/attribute-types.enum';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { AttributeModel } from '../../../../../Common/Misc/models/attribute.model';
import { DistributionStatus } from '../../../../../Common/TargetingShared/enum/distribution-status.enum';
import { TargetingRhpDialogService } from '../../../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { ContactService } from '../../../../../Common/TargetingShared/services/contact-service';
import { DistributionErrors, DistributionService, DistributionValidationLevel } from '../../../../../Common/TargetingShared/services/distribution.service';

import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../../../Core/Users/enums/user-permissions.enum';
import { UserStateModel } from '../../../../../Core/Users/models/user-state.model';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';

import { DiscoverRoutingService } from '../../../../DiscoverRouter/services/discover-routing.service';

import { AmplificationStatusEnum, amplificationStatusToApi } from '../../../enums/amplification-status.enum';
import { DistributionModel } from '../../../models/distribution.model';
import { TargetingResultModel } from '../../../models/targeting-result.model';
import { TargetingListService } from '../../../services/targeting-list.service';
import { TargetingSearchesService } from '../../../services/targeting-searches.service';
import { OnlineBoostConfirmationDialogComponent } from '../OnlineBoostConfirmationDialog/online-boost-confirmation-dialog.component';
import { DistributionReportStat } from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { DistributionReportService } from './distribution.report.service';
import { AdditionalRecipientSearchService } from './RecipientSearch/additional-recipient-search.service';
import { RecipientSearchService } from './RecipientSearch/recipient-search.service';
import { RecipientRhpTakeoverEvent } from './RecipientSearch/RecipientCard/recipient-card.component';
import { DistributionPlainTextService } from '../PlainTextEditorDialog/plain-text.service';
import { StatisticsReportType } from '../../../../../Common/TargetingShared/enum/statistics-report-type.enum';
import { StatisticsReportService } from '../../../../../Common/TargetingShared/services/statistics.report.service';
import {
    IndividualRecipientReportDataModel
} from '../../../../../Common/TargetingShared/components/statisticReport/RecipientReport/recipient.report.data.model';
import { TargetingListList } from '../../../models/targeting-list-list.model';
import { TargetingSearchList } from '../../../models/targeting-search-list.model';
import { StatTypeChangeInterface } from '../../../../../Common/TargetingShared/model/statisticsReport/stat-type-change.interface.model';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '@Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '@Core/Dialogs/enums/overlay-animation.enum';

export enum DistributionReportRHPType {
    details,
    recipients
}

@Component({
    selector: 'agpr-distribution-report',
    templateUrl: './distribution.report.component.html',
    styleUrls: ['./distribution.report.component.scss'],
    providers: [
        {
            provide: StatisticsReportService,
            useClass: DistributionReportService
        },
        {
            provide: MediaServiceApi,
            useClass: MediaServiceApi
        }
    ],
    standalone: false
})
export class DistributionReportComponent implements OnDestroy {
    private subscriptions: Subscription[] = [];

    readonly statisticsReportType = StatisticsReportType;
    readonly buttonTypes = ButtonTypesEnum;
    readonly statTypes = DistributionReportStat;
    readonly entityTypes = EntityType;

    selectedTabIndex: StatisticsReportType = StatisticsReportType.performance;
    selectedRHPTabIndex: number = DistributionReportRHPType.recipients;
    selectedEntityType: EntityType;
    sidebarExpanded: boolean = false;
    rhpTakeOver: boolean = false;
    searchEntity: TargetingResultModel;
    messageSeverity = messageSeverityEnum;

    userState: UserStateModel;
    userHasNewsroomPermission = false;
    userHasNewswirePermission = false;
    userIsWhiteLabeled = false;

    showExportButton: boolean;
    distribution: DistributionModel;
    readonly distributionStatuses = DistributionStatus;
    routeId: number;

    reportLoading = false;
    distribLoading = false;

    distributionDetailsAttributes: AttributeModel[] = [];
    distributionAttributes: AttributeModel[] = [];
    otherSettingsAttributes: AttributeModel[] = [];
    attachmentNames: string[] = [];
    errors: DistributionErrors = new DistributionErrors();
    distributionDirty: boolean = false;
    enableMppEstimates: boolean = false;
    isScheduled: boolean = false;

    bodyPlainText: string = '';
    footerPlainText: string = '';
    bodyHtml: string = '';

    distributionScheduledDate: Date;

    selectedType: DistributionReportStat;
    selectedIndividualRecipient: IndividualRecipientReportDataModel;

    targetingLists: TargetingListList[];
    targetingSearches: TargetingSearchList[];

    boosted: boolean = false;

    @ViewChild('extendedContent') public extendedContent: TemplateRef<any>;

    constructor(
        private discoverRoutingService: DiscoverRoutingService,
        private messageService: MessageService,
        public coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public distributionReportService: StatisticsReportService,
        public targetingSearchesService: TargetingSearchesService,
        public targetingListService: TargetingListService,
        public contactService: ContactService,
        public recipientSearchService: RecipientSearchService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public distributionService: DistributionService,
        public mediaServiceApi: MediaServiceApi,
        public additionalRecipientSearchService: AdditionalRecipientSearchService,
        private currentUserStateService: CurrentUserStateService,
        private configService: ConfigService,
        public titleService: Title,
        public userPreferenceService: UserPreferencesService,
        private amplifyContentService: AmplifyContentService,
        private plainTextService: DistributionPlainTextService,
        private configurationService: ConfigService
    ) {
        this.subscriptions.push(

            this.distributionReportService.resourceBody$.pipe(filter(x => !!x)).subscribe(body => {
                this.bodyPlainText = body.bodyPlainText || this.plainTextService.generateBodyPlainText(this.distribution.blocks);
                this.bodyPlainText = this.distributionService.renderFallbackTagsToDefault(this.distributionService.processMergeTags(this.bodyPlainText, []));
                this.footerPlainText = body.footerPlainText || this.plainTextService.footerSeparator.repeat(40) + '\n\n' +
                    this.plainTextService.generateFooterPlainText(this.distribution.blocks);
            }),
            this.distributionReportService.loading$.subscribe(x => {
                requestAnimationFrame(() => {
                    this.reportLoading = x;
                    if (!this.reportLoading) {
                        this.checkIfDraft();
                    }
                });
            }),
            this.distributionService.getLoading().subscribe(x => this.distribLoading = x),
            this.discoverRoutingService.getCurrentRoute().subscribe(route => this.routeId = Number(route?.value)),
            this.distributionService.getFocusedResource().pipe(filter(x => !!x)).subscribe(x => {
                if (this.distribution?.id !== x.id) {
                    this.selectedType = this.statTypes.SENT;
                }
                this.distribution = x;
                this.distributionScheduledDate = new Date(this.distribution.deliverySchedule.scheduledDateUTC);
                this.isScheduled = (new Date() < this.distributionScheduledDate) && (x.status === DistributionStatus.scheduled);
                if (this.isScheduled) {
                    this.selectedTabIndex = StatisticsReportType.composition;
                }

                this.clearNameErrorMessages();
                this.loadAttachmentChips();
                this.setAttributes();
                this.selectStatType(this.selectedType);
                this.titleService.setTitle(
                    this.localizationService.transform('Discover.distribution.browserTitle.name', {name: x.name})
                );
            }),
            this.distributionService.getFocusedResource().pipe(
                filter(x => !!x),
                switchMap((distribution: DistributionModel) => {
                    return this.distributionService.getDistributionPostOptions(distribution.id);
                })
            ).subscribe(options => {
                this.boosted = options.includes(AmplificationStatusAPIEnum.ONLINE_BOOST);
            }),
            this.distributionService.getDirtyFlag().subscribe(dirty => {
                this.distributionDirty = dirty;
            }),
            this.contactService.getFocusedRhpEntity().subscribe(entity => this.searchEntity = entity),
            this.distributionReportService.recipientReportData$.pipe().subscribe(x => {
                this.showExportButton = !!x && x.deliveredCount > 0;
            }),
            this.currentUserStateService.getState().subscribe(
                userState => {
                    this.userState = userState;
                    this.userHasNewsroomPermission = !!this.userState.permissions.find(permission => permission === userPermissionsEnum.newsroom);
                    this.userHasNewswirePermission = !!this.userState.permissions.find(permission => permission === userPermissionsEnum.newswireplus);
                }),
            this.userPreferenceService.getUserAppPreferences().pipe(filter(x => !!x)).subscribe(userPreferences => {
                this.enableMppEstimates = userPreferences.targetingPrefs.distributionsPrefs.enableMppEstimates;
            }),
            combineLatest([
                this.distributionService.getFocusedResource().pipe(filter(x => !!x)),
                this.distributionReportService.resourceBody$.pipe(filter(x => !!x))
            ]).subscribe(([distribution, body]) => {
                // Removing the tags and replacing them with default values
                this.bodyHtml = this.distributionService.renderFallbackTagsToDefault(this.distributionService.processMergeTags(body.bodyHtml, []));

                let  viewInBrowserUrl = null;
                if (distribution.includeViewInBrowserLink && distribution.guid) {
                    viewInBrowserUrl = this.configurationService.getConfig().apiUrl.split('/api/v1')[0] +
                        '/distributions/history/' + distribution.guid;

                    this.bodyHtml = this.bodyHtml.replace('\[%VIEW_IN_BROWSER_URL%\]', viewInBrowserUrl);
                }
            }),
            this.targetingListService.all$.subscribe(lists => this.targetingLists = lists),
            this.targetingSearchesService.all$.subscribe(searches => this.targetingSearches = searches),
            this.recipientSearchService.getSelectedStatType().subscribe(x => this.selectedType = x),
            this.recipientSearchService.getSelectedIndividualRecipient().subscribe(x => this.selectedIndividualRecipient = x),
        );

        if (!!this.configService.getConfig().brandGUID) {
            this.userIsWhiteLabeled = true;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    // a function that checks to see if the distribution is still set as draft, if so, go to distribution page
    checkIfDraft() {
        if (
            this.distribution &&
            (this.distribution.status === DistributionStatus.draft) &&
            (!this.reportLoading) &&
            (this.routeId === this.distribution.id)
        ) {
            this.discoverRoutingService.gotoDistribution(this.distribution.id);
        }
    }

    tabChange(index: StatisticsReportType) {
        this.selectedTabIndex = index;
        if (this.selectedTabIndex === StatisticsReportType.recipients) {
            this.selectedRHPTabIndex = DistributionReportRHPType.recipients;
        }
    }

    statTypeSelected(statTypeChange: StatTypeChangeInterface) {
        this.selectedRHPTabIndex = DistributionReportRHPType.recipients;
        const data = statTypeChange.data ? statTypeChange.data : null;
        this.recipientSearchService.setStatType(statTypeChange.statType, data);
        this.additionalRecipientSearchService.setStatType(statTypeChange.statType);
    }

    exportReport() {
        this.distributionReportService.downloadReport(this.distribution.name);
    }

    selectStatType(statType: DistributionReportStat) {
        this.recipientSearchService.setStatType(statType, null);
        this.additionalRecipientSearchService.setStatType(statType);
    }

    toggleRhpTakeover(event: RecipientRhpTakeoverEvent): void {
        const result = event.entity;
        let contactId = null;
        let outletId = null;
        if (result.entityType === EntityType.CONTACT) {
            contactId = result.id;
            if (result.selectedEmployment && result.selectedEmployment.outlet) {
                outletId = result.selectedEmployment.outlet.id;
            }
        } else {
            outletId = result.id;
        }
        this.targetingRhpDialogService.openTakeOver(
            {
                inputs: {
                    contactId: contactId,
                    outletId: outletId,
                    entityType : event.selectedEntityType,
                    isPrivate: result.isPrivate
                }
            }
        );
    }

    loadAttachmentChips() {
        if (this.distribution.attachments.length > 0) {
            // note : There is no guarantee all the uuids will be found in the media service
            this.mediaServiceApi.getFiles(this.distribution.attachments).subscribe(mediaResults => {
                this.attachmentNames.length = 0;
                mediaResults.mediaResults.forEach(attachmentMetadata => {
                    this.attachmentNames.push(attachmentMetadata.name);
                });
            });
        }
    }

    setAttributes() {
        if (!this.distribution) {
            this.distributionDetailsAttributes = [];
            this.distributionAttributes = [];
            return;
        }

        if (!this.distribution.createdDate) {
            this.distributionDetailsAttributes = [];
            this.distributionAttributes = [];
            return;
        }

        const createdByUserAttribute: AttributeModel = {
            id: 0,
            label: 'Discover.targeting.panels.createdUser',
            value: this.distribution.createdByUserId,
            type: attributeTypesEnum.user,
        };

        const createdOnDateAttribute: AttributeModel = {
            id: 1,
            label: 'Discover.targeting.panels.createdDate',
            value: this.distribution.createdDate,
            type: attributeTypesEnum.date,
        };

        const lastModifiedByUserAttribute: AttributeModel = {
            id: 2,
            label: 'Discover.targeting.panels.modifiedUser',
            value: this.distribution.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDateAttribute: AttributeModel = {
            id: 3,
            label: 'Discover.targeting.panels.modifiedDate',
            value: this.distribution.modifiedDate,
            type: attributeTypesEnum.date,
        };

        const scheduledDateAttribute: AttributeModel = {
            id: 4,
            label: 'Discover.distribution.details.scheduledDate',
            value: this.distribution.sentNow ?
                'Discover.distribution.details.immediate'
                : this.distribution.deliverySchedule.scheduledDateUTC,
            type: this.distribution.sentNow ?
                attributeTypesEnum.simple
                : attributeTypesEnum.date,
            timezone: this.distribution.deliverySchedule.timeZone
        };

        const sentDateAttribute: AttributeModel = {
            id: 5,
            label: 'Discover.distribution.details.sentDate',
            value: this.distribution.sentDate,
            type: attributeTypesEnum.date
        };

        const sentFromAttribute: AttributeModel = {
            id: 6,
            label: 'Discover.distribution.details.sentFrom',
            value: this.distribution.sendFrom?.connectionEmail,
            type: attributeTypesEnum.simple
        };

        const sentByAttribute: AttributeModel = {
            id: 7,
            label: 'Discover.distribution.details.sentBy',
            value: this.distribution.sentUserId,
            type: attributeTypesEnum.user
        };

        this.distributionDetailsAttributes = [
            createdByUserAttribute,
            createdOnDateAttribute,
            lastModifiedByUserAttribute,
            lastModifiedDateAttribute,
            scheduledDateAttribute,
            sentDateAttribute,
            sentFromAttribute,
            sentByAttribute
        ].filter(x => !!x.value);

        if (this.distribution?.amplification?.length > 0){
            const value = this.distribution.amplification
                .map(amplification => this.localizationService.transform(`Discover.distribution.amplification.${amplification}`));

            const sentAmplification: AttributeModel = {
                id: 8,
                label: 'Discover.distribution.details.amplification',
                value: value,
                type: attributeTypesEnum.simpleList
            };
            this.distributionDetailsAttributes.push(sentAmplification);
        }

        const fromNameAttribute: AttributeModel = {
            id: 0,
            label: 'Discover.targeting.distribution.header.fromName',
            value: this.distribution.fromName,
            type: attributeTypesEnum.simple
        };

        const replyEmailAttribute: AttributeModel = {
            id: 1,
            label: 'Discover.targeting.distribution.header.replyEmail',
            value: this.distribution.replyEmail,
            type: attributeTypesEnum.simple,
        };

        const clickTrackingValue = this.distribution.clickTracking
            ? this.localizationService.transform('Common.enabled')
            : this.localizationService.transform('Common.disabled');
        const clickTrackingAttribute: AttributeModel = {
            id: 2,
            label: 'Discover.targeting.distribution.header.clickTracking',
            value: clickTrackingValue,
            type: attributeTypesEnum.simple,
        };

        const openTrackingValue = this.distribution.openTracking
            ? this.localizationService.transform('Common.enabled')
            : this.localizationService.transform('Common.disabled');
        const openTrackingAttribute: AttributeModel = {
            id: 3,
            label: 'Discover.targeting.distribution.header.openTracking',
            value: openTrackingValue,
            type: attributeTypesEnum.simple,
        };

        this.distributionAttributes = [
            fromNameAttribute,
            replyEmailAttribute,
        ];

        this.otherSettingsAttributes = [
            clickTrackingAttribute,
            openTrackingAttribute
        ];
    }

    clearNameErrorMessages() {
        this.errors.nameExists = false;
        this.errors.hasNoName = false;
        this.distributionService.updateDirtyFlag();
    }

    validateAndSetErrorMessages(): boolean {
        this.errors = this.distributionService.getValidationErrors(this.distribution, DistributionValidationLevel.SENT);
        this.distributionService.setValidationErrors$(this.errors);
        return this.errors.getErrorCount() > 0;
    }

    saveDistribution() {
        this.distribution.name = this.distribution.name.trim();
        if (this.validateAndSetErrorMessages()) {
            return;
        }
        this.distributionService.saveDistribution().subscribe();
    }

    openDisclaimerDialog() {
        if (this.boosted) {
            return;
        }
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const overlayContainer = document.getElementById('main-content');
        const dialogIO = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        if (this.distribution?.amplification?.length > 0) {
                            if (!this.distribution.amplification.includes(AmplificationStatusEnum.ONLINE_BOOST)) {
                                this.distribution.amplification.push(AmplificationStatusEnum.ONLINE_BOOST);
                            } else {
                                // This shouldn't happen
                                throw new Error('Distribution already has Online Boost');
                            }
                        } else {
                            this.distribution.amplification = [AmplificationStatusEnum.ONLINE_BOOST];
                        }

                        const amplifications: AmplificationStatusAPIEnum[] = [];
                        this.distribution.amplification.forEach(amplification => {
                            amplifications.push(amplificationStatusToApi.get(amplification));
                        });

                        this.distributionService.setDistributionPostOptions(this.distribution.id, amplifications).subscribe((result) => {
                            this.setAttributes();

                            this.messageService.add({
                                severity : messageSeverityEnum.success,
                                summary: 'Discover.onlineBoost.growlMessage.heading',
                                detail: 'Discover.onlineBoost.growlMessage.text'
                            });
                        });

                    }
                }
            }
        };
        this.coreDialogService.open(OnlineBoostConfirmationDialogComponent, dialogIO);
    }

    changeMppEstimates(event) {
        this.enableMppEstimates = event;
    }

    setSelectLink(link: string): void {
        this.recipientSearchService.setLink(link);
    }
}
